import React, {Component} from 'react';

import classNames from 'classnames';

import {
    CODE_FILE_UNDEFINED_ERROR,
    CODE_FILE_SIZE_ERROR,
    CODE_FILE_TYPE_ERROR,
} from 'constants/file/fileCode';
import {checkFileSize, checkFileType, ValidFileType} from 'libs/uploader/uploader';

import {ErrorType} from 'types/successPage';

import s from './uploader.scss';

export type ChangeResultType =
    | {type: 'error'; error_type: ErrorType}
    | {type: 'success'; file: File; fileSrc: string};

type PropsType = {
    id: string;
    maxSize: number;
    fileTypes: Array<ValidFileType>;
    handleChange: (data: ChangeResultType) => void;
    children?: React.ReactNode;
    disabled?: boolean;
    isLabelFocused?: boolean;
};

/**
 *  Default class for Uploader file
 */
export class Uploader extends Component<PropsType> {
    static defaultProps = {
        children: null,
        disabled: false,
        isLabelFocused: false,
    };

    /**
     * Set current file status and return in parent component result
     *
     * @param {string} status Status (e.g. 'error')
     * @param {ErrorType} type Error code
     * @returns {boolean} false
     */
    setFileStatus(status: 'error', type: ErrorType): false {
        this.props.handleChange({error_type: type, type: status});

        return false;
    }

    /**
     * Validation input file
     *
     * @param {File} file Uploaded file object
     * @returns {boolean} Did all the checks pass
     */
    checkFile = (file: File): boolean => {
        if (!file) {
            return this.setFileStatus('error', CODE_FILE_UNDEFINED_ERROR);
        } else if (!checkFileType(file, this.props.fileTypes)) {
            return this.setFileStatus('error', CODE_FILE_TYPE_ERROR);
        } else if (!checkFileSize(file, this.props.maxSize)) {
            return this.setFileStatus('error', CODE_FILE_SIZE_ERROR);
        }

        return true;
    };

    /**
     * Change value in input
     *
     * @param {Event} event JS event
     */
    handleFileChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
        event.preventDefault();

        const {currentTarget: input} = event;

        const file = (input.files || [])[0];

        if (this.checkFile(file)) {
            const reader = new FileReader();

            reader.onloadend = (readerEvent: ProgressEvent<FileReader>) => {
                this.props.handleChange({
                    file,
                    fileSrc: readerEvent.target ? (readerEvent.target.result as string) || '' : '',
                    type: 'success',
                });
            };

            reader.readAsDataURL(file);
        }

        input.value = '';
    };

    /**
     * Render jsx to html
     *
     * @returns {Node} Rendered react component
     */
    render() {
        const {children, disabled, fileTypes, id, isLabelFocused} = this.props;

        if (disabled) return children;

        return (
            <label className={classNames({[s.label]: isLabelFocused})} htmlFor={id}>
                <input
                    accept={fileTypes.join(',')}
                    className={s.input}
                    data-testid="file-upload"
                    id={id}
                    name={id}
                    onChange={this.handleFileChange}
                    type="file"
                />
                {children}
            </label>
        );
    }
}
